import { useState, useRef, useEffect, useCallback } from "react";
import { UploadCloud, Loader2, Play, Pause, Download, RotateCcw, Wand2, X, Music2, Save, Link2, Trash2, FolderPlus, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import MasteringAssistant, { pollMasteringTask } from "./MasteringAssistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same per-device guest identity scheme as App.js's getGuestEmail() - kept in
// sync so a caller that forgets to pass userEmail still gets an isolated
// identity instead of the old shared literal guest@sounddna.app address.
function getGuestEmail() {
  let id = localStorage.getItem('sounddna_guest_id');
  if (!id) {
    id = Math.random().toString(36).slice(2, 10);
    localStorage.setItem('sounddna_guest_id', id);
  }
  return `guest-${id}@sounddna.app`;
}

const MIXING_PRESETS = [
  { id: "auto_level", name: "Auto-Level", desc: "Balances volume automatically", icon: "🎚️" },
  { id: "drum_punch", name: "Drum Punch", desc: "Harder kick & snappy snares", icon: "🥁" },
  { id: "vocal_clarity", name: "Vocal Clarity", desc: "Cuts mud, lifts the voice", icon: "🎤" },
  { id: "stereo_width", name: "Stereo Width", desc: "Wider, more open image", icon: "↔️" },
  { id: "buss_glue", name: "Buss Glue", desc: "Glues the whole mix together", icon: "🧲" },
];

const MASTERING_PRESETS = [
  { id: "radio_ready", name: "Radio Ready", desc: "Bright, loud, broadcast polish", icon: "📻" },
  { id: "club_maximizer", name: "Club Maximizer", desc: "Heavy low-end, max loudness", icon: "🔊" },
  { id: "tape_warmth", name: "Tape Warmth", desc: "Analog saturation & warmth", icon: "📼" },
  { id: "clean_limit", name: "Clean Limit", desc: "Transparent, dynamics intact", icon: "✨" },
  { id: "high_end_air", name: "High-End Air", desc: "Silky sparkling top end", icon: "💨" },
];

const ALL_PRESETS = [...MIXING_PRESETS, ...MASTERING_PRESETS];
const AUDIO_EXTS = ["mp3", "wav", "flac", "ogg", "m4a", "aiff", "aif"];

const presetName = (id) => ALL_PRESETS.find(p => p.id === id)?.name || id;

function PresetButton({ preset, selected, onToggle, accent, disabled }) {
  return (
    <button
      onClick={() => onToggle(preset.id)}
      disabled={disabled}
      data-testid={`mastering-preset-${preset.id.replace(/_/g, '-')}`}
      style={{
        display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left',
        padding: '12px 14px', borderRadius: '12px', cursor: disabled ? 'not-allowed' : 'pointer',
        background: selected ? `${accent}22` : 'rgba(255,255,255,0.04)',
        border: selected ? `1px solid ${accent}` : '1px solid rgba(255,255,255,0.1)',
        opacity: disabled ? 0.4 : 1,
        transition: 'background-color 0.15s, border-color 0.15s, transform 0.1s'
      }}
    >
      <span style={{ fontSize: '1.2rem' }}>{preset.icon}</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: selected ? accent : '#fff' }}>{preset.name}</span>
        <span style={{ display: 'block', fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>{preset.desc}</span>
      </span>
      <span style={{
        width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
        border: selected ? `5px solid ${accent}` : '2px solid rgba(255,255,255,0.25)',
        transition: 'border 0.15s'
      }} />
    </button>
  );
}

function Waveform({ waveform, mode, audioRef }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!waveform || !canvasRef.current) return undefined;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height, mid = H / 2;
    const active = mode === 'after' ? waveform.after : waveform.before;
    const ghost = mode === 'after' ? waveform.before : waveform.after;
    const activeRGB = mode === 'after' ? '34,197,94' : '245,158,11';

    // Guard: if the backend response is missing one side of the waveform
    // (e.g. mastering failed partway), `active`/`ghost` won't be arrays.
    // Drawing against them used to throw during the render loop with no
    // error boundary in place, which unmounted the whole app (black screen).
    if (!Array.isArray(active) || !Array.isArray(ghost)) {
      ctx.clearRect(0, 0, W, H);
      return undefined;
    }

    const draw = () => {
      const audio = audioRef.current;
      const progress = audio && audio.duration ? audio.currentTime / audio.duration : 0;
      ctx.clearRect(0, 0, W, H);
      const n = active.length;
      if (n === 0) return;
      const bw = W / n;
      for (let i = 0; i < n; i++) {
        const x = i * bw;
        const gh = Math.max(1, (ghost[i] || 0) * mid * 0.95);
        ctx.fillStyle = 'rgba(255,255,255,0.10)';
        ctx.fillRect(x, mid - gh, Math.max(1, bw - 1), gh * 2);
        const ah = Math.max(1.5, (active[i] || 0) * mid * 0.95);
        ctx.fillStyle = `rgba(${activeRGB},${i / n <= progress ? 0.95 : 0.45})`;
        ctx.fillRect(x, mid - ah, Math.max(1, bw - 1), ah * 2);
      }
      if (progress > 0) {
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fillRect(progress * W - 1, 0, 2, H);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [waveform, mode, audioRef]);

  if (!waveform) return null;

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
  };

  const activeColor = mode === 'after' ? '#22c55e' : '#f59e0b';
  return (
    <div data-testid="mastering-waveform" style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px', padding: '12px 14px 8px', marginBottom: '14px'
    }}>
      <canvas
        ref={canvasRef} width={1000} height={140} onClick={seek}
        data-testid="mastering-waveform-canvas"
        style={{ width: '100%', height: '84px', display: 'block', cursor: 'pointer' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>
          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', background: activeColor, marginRight: '5px', verticalAlign: 'middle' }} />
          {mode === 'after' ? 'Mastered' : 'Original'}
          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', background: 'rgba(255,255,255,0.15)', margin: '0 5px 0 12px', verticalAlign: 'middle' }} />
          {mode === 'after' ? 'Original (ghost)' : 'Mastered (ghost)'}
        </span>
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>Click waveform to seek</span>
      </div>
    </div>
  );
}

function LoudnessMeter({ loudness }) {
  if (!loudness) return null;
  const barPct = (db) => Math.max(4, Math.min(100, ((db + 40) / 40) * 100));
  const gain = loudness.gain_db;
  const row = (label, stats, color, testId) => (
    <div style={{ marginBottom: '10px' }} data-testid={testId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: '1px' }}>{label}</span>
        <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
          <b style={{ color }}>{stats.rms_db} dB</b> RMS · {stats.peak_db} dB peak
        </span>
      </div>
      <div style={{ height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <div style={{ width: `${barPct(stats.rms_db)}%`, height: '100%', borderRadius: '4px', background: color, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
  return (
    <div data-testid="mastering-loudness-meter" style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px', padding: '14px 16px', marginBottom: '14px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#fff' }}>📊 Loudness</span>
        <span data-testid="mastering-loudness-gain" style={{
          padding: '3px 10px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 800,
          background: gain >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
          color: gain >= 0 ? '#22c55e' : '#f59e0b',
          border: `1px solid ${gain >= 0 ? 'rgba(34,197,94,0.4)' : 'rgba(245,158,11,0.4)'}`
        }}>
          {gain >= 0 ? '+' : ''}{gain} dB {gain >= 0 ? 'louder' : 'quieter'}
        </span>
      </div>
      {row('BEFORE', loudness.before, '#f59e0b', 'mastering-loudness-before')}
      {row('AFTER', loudness.after, '#22c55e', 'mastering-loudness-after')}
    </div>
  );
}

export default function MasteringStudio({ onBack, userEmail = getGuestEmail(), onAddToWorkflow }) {
  const [session, setSession] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selected, setSelected] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState(null);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState('after');
  const [playing, setPlaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [chains, setChains] = useState([]);
  const [showChainInput, setShowChainInput] = useState(false);
  const [chainName, setChainName] = useState('');
  const [savingChain, setSavingChain] = useState(false);
  const [savedToUploads, setSavedToUploads] = useState(false);
  const [savingToUploads, setSavingToUploads] = useState(false);
  const [addedToWorkflow, setAddedToWorkflow] = useState(false);
  const [studioTab, setStudioTab] = useState('assistant');
  const audioRef = useRef(null);
  const inputRef = useRef(null);

  const handleAssistantResult = (r) => {
    setResult(r); setMode('after'); setPlaying(false);
    setSavedToUploads(false); setAddedToWorkflow(false);
  };

  const loadChains = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mastering/chains`, { params: { email: userEmail } });
      setChains(res.data.chains || []);
    } catch (e) { /* silent */ }
  }, [userEmail]);

  useEffect(() => { loadChains(); }, [loadChains]);

  const uploadFile = async (file) => {
    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (!AUDIO_EXTS.includes(ext)) {
      toast.error('Unsupported format. Use MP3, WAV, FLAC, OGG, M4A or AIFF');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large (max 100MB)');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setResult(null);
    setSelected([]);
    setSavedToUploads(false);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/mastering/upload`, form, {
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / (e.total || 1)) * 100))
      });
      setSession(res.data);
      toast.success(`🎧 "${file.name}" loaded into the studio`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed - please try again');
    } finally {
      setUploading(false);
    }
  };

  const togglePreset = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const processAudio = async (presetsOverride, sessionOverride) => {
    const presets = presetsOverride || selected;
    const sess = sessionOverride || session;
    if (!sess || presets.length === 0) return;
    setProcessing(true);
    setPlaying(false);
    setProcessError(null);
    try {
      const res = await axios.post(`${API}/mastering/process`, {
        session_id: sess.session_id,
        presets
      });
      const data = await pollMasteringTask(res.data.task_id);
      setResult(data);
      setMode('after');
      setSavedToUploads(false);
      setAddedToWorkflow(false);
      toast.success(`✨ Master complete: ${data.presets_applied.join(' + ')}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Processing failed - please try again';
      setProcessError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const saveChain = async () => {
    const name = chainName.trim();
    if (!name) { toast.error('Give your chain a name first'); return; }
    setSavingChain(true);
    try {
      const res = await axios.post(`${API}/mastering/chains`, {
        name, presets: selected, email: userEmail
      });
      setChains(prev => [res.data.chain, ...prev]);
      setChainName('');
      setShowChainInput(false);
      toast.success(`🔗 Chain "${name}" saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save chain');
    } finally {
      setSavingChain(false);
    }
  };

  const applyChain = (chain) => {
    setSelected(chain.presets);
    if (session && !processing) {
      toast.info(`🔗 Applying "${chain.name}"...`);
      processAudio(chain.presets);
    }
  };

  const deleteChain = async (chain, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/mastering/chains/${chain.chain_id}`);
      setChains(prev => prev.filter(c => c.chain_id !== chain.chain_id));
      toast.success(`Chain "${chain.name}" deleted`);
    } catch (err) {
      toast.error('Failed to delete chain');
    }
  };

  const saveToUploads = async () => {
    if (!result || savedToUploads) return;
    setSavingToUploads(true);
    try {
      const base = (session?.filename || 'track').replace(/\.[^.]+$/, '');
      const res = await axios.post(`${API}/mastering/save-to-uploads`, {
        filename: result.processed_filename,
        email: userEmail,
        title: `${base} (Mastered)`
      });
      setSavedToUploads(true);
      toast.success(`📚 "${res.data.title}" saved to My Uploads`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save to My Uploads');
    } finally {
      setSavingToUploads(false);
    }
  };

  const addToWorkflow = () => {
    if (!result || !onAddToWorkflow || addedToWorkflow) return;
    const base = (session?.filename || 'track').replace(/\.[^.]+$/, '');
    onAddToWorkflow({
      url: result.processed_url,
      filename: `${base}_mastered.wav`,
      presets: (result.presets_applied || []).join(' + ')
    });
    setAddedToWorkflow(true);
  };

  const currentSrc = () => {
    if (!session) return '';
    const path = mode === 'after' && result ? result.processed_url : session.original_url;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  };

  const switchMode = (m) => {
    if (m === mode || !audioRef.current) { setMode(m); return; }
    const t = audioRef.current.currentTime;
    const wasPlaying = !audioRef.current.paused;
    setMode(m);
    setTimeout(() => {
      if (!audioRef.current) return;
      audioRef.current.currentTime = t;
      if (wasPlaying) audioRef.current.play().catch(() => {});
    }, 60);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) { audioRef.current.play().catch(() => {}); setPlaying(true); }
    else { audioRef.current.pause(); setPlaying(false); }
  };

  const downloadMaster = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${result.processed_url}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = (session?.filename || 'track').replace(/\.[^.]+$/, '');
      a.download = `${base}_mastered.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('💾 16-bit WAV downloaded - Suno ready!');
    } catch (e) {
      toast.error('Download failed - please try again');
    } finally {
      setDownloading(false);
    }
  };

  const reset = () => {
    setSession(null); setResult(null); setSelected([]); setPlaying(false); setMode('after');
    setSavedToUploads(false); setShowChainInput(false); setProcessError(null);
  };

  const abBtn = (m, label, color) => (
    <button
      onClick={() => switchMode(m)}
      data-testid={`mastering-comparison-${m}-btn`}
      style={{
        flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
        background: mode === m ? color : 'rgba(255,255,255,0.05)',
        border: mode === m ? `1px solid ${color}` : '1px solid rgba(255,255,255,0.12)',
        color: mode === m ? '#000' : 'rgba(255,255,255,0.6)',
        transition: 'background-color 0.15s, color 0.15s'
      }}
    >{label}</button>
  );

  return (
    <div data-testid="mastering-studio-view" style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <button
        onClick={onBack}
        data-testid="mastering-back-btn"
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px',
          background: 'linear-gradient(135deg, #00d4ff, #0099cc)', border: 'none', borderRadius: '10px',
          color: '#fff', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', marginBottom: '20px',
          boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
        }}
      >
        <RotateCcw className="w-4 h-4" /> ← BACK TO DASHBOARD
      </button>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff', margin: '0 0 6px 0' }}>🎛️ Mixing & Mastering Studio</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
          Drop your track, stack studio-grade presets, and A/B the result. Exports Suno-ready 16-bit WAV.
        </p>
      </div>

      {chains.length > 0 && (
        <div className="modern-card" style={{ padding: '14px 18px', marginBottom: '20px' }} data-testid="mastering-chains-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Link2 className="w-4 h-4" style={{ color: '#00d4ff' }} />
            <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.8rem', letterSpacing: '1px' }}>SAVED CHAINS</span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>— one click to reapply</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {chains.map(chain => (
              <button
                key={chain.chain_id}
                onClick={() => applyChain(chain)}
                disabled={processing}
                title={chain.presets.map(presetName).join(' + ')}
                data-testid={`mastering-chain-${chain.chain_id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', borderRadius: '999px',
                  background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.35)',
                  color: '#00d4ff', fontSize: '0.78rem', fontWeight: 700,
                  cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? 0.5 : 1,
                  transition: 'background-color 0.15s'
                }}
              >
                🔗 {chain.name}
                <span style={{
                  padding: '1px 7px', borderRadius: '999px', fontSize: '0.62rem',
                  background: 'rgba(0,212,255,0.15)', color: 'rgba(255,255,255,0.7)'
                }}>{chain.presets.length}</span>
                <span
                  onClick={(e) => deleteChain(chain, e)}
                  data-testid={`mastering-chain-delete-${chain.chain_id}`}
                  style={{ display: 'flex', color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}
                >
                  <Trash2 className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!session ? (
        <div
          data-testid="mastering-dropzone"
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragActive(false);
            const f = e.dataTransfer.files?.[0];
            if (f) uploadFile(f);
          }}
          style={{
            border: dragActive ? '2px dashed #00d4ff' : '2px dashed rgba(255,255,255,0.2)',
            borderRadius: '16px', padding: '60px 24px', textAlign: 'center', cursor: 'pointer',
            background: dragActive ? 'rgba(0,212,255,0.08)' : 'rgba(255,255,255,0.02)',
            transition: 'background-color 0.15s, border-color 0.15s'
          }}
        >
          <input
            ref={inputRef} type="file" accept=".mp3,.wav,.flac,.ogg,.m4a,.aiff,.aif" hidden
            data-testid="mastering-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }}
          />
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10" style={{ margin: '0 auto 12px', color: '#00d4ff', animation: 'spin 1s linear infinite' }} />
              <p style={{ color: '#00d4ff', fontWeight: 700, margin: 0 }}>Uploading... {uploadProgress}%</p>
            </>
          ) : (
            <>
              <UploadCloud className="w-12 h-12" style={{ margin: '0 auto 14px', color: '#00d4ff' }} />
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', margin: '0 0 6px 0' }}>Drag & drop your track here</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', margin: 0 }}>or click to browse — MP3, WAV, FLAC, OGG, M4A, AIFF (max 100MB)</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="modern-card" style={{ padding: '14px 18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Music2 className="w-5 h-5" style={{ color: '#00d4ff', flexShrink: 0 }} />
            <span data-testid="mastering-loaded-filename" style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {session.filename}
            </span>
            <button
              onClick={reset}
              data-testid="mastering-remove-file-btn"
              style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }} data-testid="mastering-tab-bar">
            {[['assistant', '🤖 Mastering Assistant'], ['presets', '🎚️ Preset Chains']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setStudioTab(id)}
                data-testid={`mastering-tab-${id}`}
                style={{
                  flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
                  background: studioTab === id ? 'linear-gradient(135deg, rgba(57,210,192,0.2), rgba(0,212,255,0.12))' : 'rgba(255,255,255,0.04)',
                  border: studioTab === id ? '1px solid #39d2c0' : '1px solid rgba(255,255,255,0.1)',
                  color: studioTab === id ? '#39d2c0' : 'rgba(255,255,255,0.55)',
                  transition: 'background-color 0.15s, color 0.15s'
                }}
              >{label}</button>
            ))}
          </div>

          {studioTab === 'assistant' && (
            <MasteringAssistant session={session} onResult={handleAssistantResult} />
          )}

          {studioTab === 'presets' && (<>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            <div className="modern-card" style={{ padding: '18px' }} data-testid="mastering-mixing-column">
              <h3 style={{ color: '#00d4ff', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '2px', margin: '0 0 14px 0' }}>MIXING</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {MIXING_PRESETS.map(p => (
                  <PresetButton key={p.id} preset={p} selected={selected.includes(p.id)} onToggle={togglePreset} accent="#00d4ff" disabled={processing} />
                ))}
              </div>
            </div>
            <div className="modern-card" style={{ padding: '18px' }} data-testid="mastering-mastering-column">
              <h3 style={{ color: '#a855f7', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '2px', margin: '0 0 14px 0' }}>MASTERING</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {MASTERING_PRESETS.map(p => (
                  <PresetButton key={p.id} preset={p} selected={selected.includes(p.id)} onToggle={togglePreset} accent="#a855f7" disabled={processing} />
                ))}
              </div>
            </div>
          </div>

          {selected.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              {showChainInput ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={chainName}
                    onChange={(e) => setChainName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !savingChain && saveChain()}
                    placeholder='Name this chain... e.g. "My Radio Master"'
                    maxLength={60}
                    autoFocus
                    data-testid="mastering-chain-name-input"
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: '10px', fontSize: '0.85rem',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,212,255,0.4)', color: '#fff', outline: 'none'
                    }}
                  />
                  <button
                    onClick={saveChain}
                    disabled={savingChain}
                    data-testid="mastering-chain-save-confirm-btn"
                    style={{
                      padding: '10px 18px', borderRadius: '10px', border: 'none', fontWeight: 800, fontSize: '0.8rem',
                      background: '#00d4ff', color: '#000', cursor: savingChain ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                  >
                    {savingChain ? <Loader2 className="w-4 h-4" style={{ animation: 'spin 1s linear infinite' }} /> : <Save className="w-4 h-4" />} Save
                  </button>
                  <button
                    onClick={() => { setShowChainInput(false); setChainName(''); }}
                    data-testid="mastering-chain-save-cancel-btn"
                    style={{
                      padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)',
                      background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.8rem'
                    }}
                  >Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setShowChainInput(true)}
                  data-testid="mastering-save-chain-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '999px',
                    background: 'rgba(0,212,255,0.08)', border: '1px dashed rgba(0,212,255,0.5)',
                    color: '#00d4ff', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  <Save className="w-4 h-4" /> Save these {selected.length} preset{selected.length > 1 ? 's' : ''} as a chain
                </button>
              )}
            </div>
          )}

          <button
            onClick={() => processAudio()}
            disabled={processing || selected.length === 0}
            data-testid="mastering-process-btn"
            style={{
              width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
              fontWeight: 800, fontSize: '1rem', marginBottom: '20px',
              cursor: processing || selected.length === 0 ? 'not-allowed' : 'pointer',
              background: selected.length === 0 ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #00d4ff, #a855f7)',
              color: selected.length === 0 ? 'rgba(255,255,255,0.35)' : '#000',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
            }}
          >
            {processing ? (
              <><Loader2 className="w-5 h-5" style={{ animation: 'spin 1s linear infinite' }} /> Processing your master...</>
            ) : (
              <><Wand2 className="w-5 h-5" /> {selected.length === 0 ? 'Select presets to begin' : `Apply ${selected.length} Preset${selected.length > 1 ? 's' : ''} & Master`}</>
            )}
          </button>
          </>)}

          {processError && (
            <div data-testid="mastering-process-error" style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', marginBottom: '20px',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.45)', borderRadius: '12px'
            }}>
              <X className="w-4 h-4" style={{ color: '#ef4444', flexShrink: 0 }} />
              <span style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 700, flex: 1 }}>{processError}</span>
              <button
                onClick={() => setProcessError(null)}
                data-testid="mastering-process-error-dismiss"
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}
              >Dismiss</button>
            </div>
          )}

          {result && (
            <div className="modern-card" style={{ padding: '20px' }} data-testid="mastering-comparison-player">
              <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, margin: '0 0 4px 0' }}>🎧 Comparison Player</h3>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', margin: '0 0 14px 0' }}>
                Applied: {(result.presets_applied || []).join(' + ')}
              </p>
              <LoudnessMeter loudness={result.loudness} />
              <Waveform waveform={result.waveform} mode={mode} audioRef={audioRef} />
              <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                {abBtn('before', 'BEFORE (Original)', '#f59e0b')}
                {abBtn('after', 'AFTER (Mastered)', '#22c55e')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={togglePlay}
                  data-testid="mastering-play-btn"
                  style={{
                    width: '46px', height: '46px', borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0,
                    background: mode === 'after' ? '#22c55e' : '#f59e0b', color: '#000',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" style={{ marginLeft: '2px' }} />}
                </button>
                <audio
                  ref={audioRef}
                  src={currentSrc()}
                  controls
                  preload="metadata"
                  data-testid="mastering-audio-element"
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  style={{ flex: 1, height: '40px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
                <button
                  onClick={downloadMaster}
                  disabled={downloading}
                  data-testid="mastering-download-btn"
                  style={{
                    flex: '1 1 240px', padding: '14px', borderRadius: '10px', border: 'none',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#000', fontWeight: 800,
                    fontSize: '0.85rem', cursor: downloading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}
                >
                  {downloading ? <Loader2 className="w-4 h-4" style={{ animation: 'spin 1s linear infinite' }} /> : <Download className="w-4 h-4" />}
                  Download WAV (16-bit / 44.1kHz)
                </button>
                <button
                  onClick={saveToUploads}
                  disabled={savingToUploads || savedToUploads}
                  data-testid="mastering-save-to-uploads-btn"
                  style={{
                    flex: '1 1 240px', padding: '14px', borderRadius: '10px',
                    border: savedToUploads ? '1px solid rgba(34,197,94,0.5)' : '1px solid rgba(0,212,255,0.5)',
                    background: savedToUploads ? 'rgba(34,197,94,0.1)' : 'rgba(0,212,255,0.1)',
                    color: savedToUploads ? '#22c55e' : '#00d4ff', fontWeight: 800,
                    fontSize: '0.85rem', cursor: savedToUploads ? 'default' : savingToUploads ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}
                >
                  {savingToUploads ? <Loader2 className="w-4 h-4" style={{ animation: 'spin 1s linear infinite' }} />
                    : savedToUploads ? <Check className="w-4 h-4" /> : <FolderPlus className="w-4 h-4" />}
                  {savedToUploads ? 'Saved to My Uploads ✓' : 'Save to My Uploads'}
                </button>
                {onAddToWorkflow && (
                  <button
                    onClick={addToWorkflow}
                    disabled={addedToWorkflow}
                    data-testid="mastering-add-to-workflow-btn"
                    style={{
                      flex: '1 1 240px', padding: '14px', borderRadius: '10px',
                      border: addedToWorkflow ? '1px solid rgba(34,197,94,0.5)' : '1px solid rgba(168,85,247,0.5)',
                      background: addedToWorkflow ? 'rgba(34,197,94,0.1)' : 'rgba(168,85,247,0.1)',
                      color: addedToWorkflow ? '#22c55e' : '#a855f7', fontWeight: 800,
                      fontSize: '0.85rem', cursor: addedToWorkflow ? 'default' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                    }}
                  >
                    {addedToWorkflow ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                    {addedToWorkflow ? 'In Unified Workflow ✓' : 'Add to Unified Workflow'}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
